/**
 * Ephemeral, in-memory state used to suspend/resume the web LoginDialog
 * when the user navigates away (e.g. "Raise Support Ticket") and then
 * presses Back.
 *
 * Security notes:
 * - Stored in a module-level variable (same security profile as React state —
 *   both are plain JS heap objects). Cleared on page refresh.
 * - A TTL is enforced: suspended state expires after MAX_SUSPEND_TTL_MS.
 *   This prevents a stale JWT from being restored if the user walks away.
 * - Sensitive fields (jwtToken, transactionId) are only stored when necessary
 *   and are wiped on consume or expiry.
 * - Password is intentionally NOT stored — it's not needed for the OTP step.
 */

const MAX_SUSPEND_TTL_MS = 5 * 60 * 1000; // 5 minutes

export interface SuspendedAuthFlowState {
    isSuspended: boolean;
    suspendedAt?: number;
    flowType?: 'web_login';
    // Web LoginDialog state (OTP flow)
    webStep?: string;
    webEmiratesId?: string;
    webMobileOtp?: string;
    webEmailOtp?: string;
    webTempPhoneNumber?: string;
    webTempEmail?: string;
    webTempJwtToken?: string;
    /** jwtToken returned by send-OTP; sent as the verify-OTP bearer. */
    webOtpJwtToken?: string;
    webTransactionId?: string;
    webResendTimer?: number;
}

const EMPTY_STATE: SuspendedAuthFlowState = { isSuspended: false };

let authFlowState: SuspendedAuthFlowState = { ...EMPTY_STATE };

function isExpired(state: SuspendedAuthFlowState): boolean {
    if (!state.suspendedAt) return true;
    return Date.now() - state.suspendedAt > MAX_SUSPEND_TTL_MS;
}

export function suspendAuthFlow(state: Omit<Partial<SuspendedAuthFlowState>, 'isSuspended' | 'suspendedAt'>) {
    authFlowState = { ...state, isSuspended: true, suspendedAt: Date.now() };
}

export function peekSuspendedAuthFlow(): SuspendedAuthFlowState | null {
    if (!authFlowState.isSuspended) return null;
    if (isExpired(authFlowState)) {
        // Expired — wipe it (including any JWT)
        authFlowState = { ...EMPTY_STATE };
        return null;
    }
    return { ...authFlowState };
}

export function consumeSuspendedAuthFlow(): SuspendedAuthFlowState | null {
    if (!authFlowState.isSuspended) return null;
    if (isExpired(authFlowState)) {
        authFlowState = { ...EMPTY_STATE };
        return null;
    }
    const state = { ...authFlowState };
    authFlowState = { ...EMPTY_STATE }; // wipe completely
    return state;
}

/** Force-clear any suspended state (e.g. when the dialog is closed normally). */
export function clearSuspendedAuthFlow(): void {
    authFlowState = { ...EMPTY_STATE };
}
